/**
 * Selectors for focus-trapping containers (MUI Modal/Dialog/Popover and any
 * ARIA dialog). When a copy is triggered from inside one of these, the temp
 * textarea must be mounted *inside* the container — see `resolveMountPoint`.
 */
const FOCUS_TRAP_SELECTOR =
  '[role="dialog"], .MuiModal-root, .MuiPopover-root, .MuiDialog-root';

/**
 * Chooses where to mount the temporary textarea used by the `execCommand`
 * fallback.
 *
 * MUI's `Modal`/`Popover` install a focus trap (the `sentinelStart` /
 * `sentinelEnd` divs). If the textarea is appended to `document.body` — outside
 * the modal's DOM subtree — the trap steals focus back the instant we focus it,
 * so `select()` + `execCommand('copy')` runs against a deselected element and
 * copies nothing. Mounting the textarea *inside* the trap avoids that fight.
 *
 * Preference order: an explicit caller-supplied container, then the nearest
 * focus-trapping ancestor of the currently focused element, then `document.body`.
 */
function resolveMountPoint(container?: HTMLElement | null): HTMLElement {
  if (container && container.isConnected) {
    return container;
  }
  try {
    const active = document.activeElement as HTMLElement | null;
    const trap = active?.closest?.(FOCUS_TRAP_SELECTOR) as HTMLElement | null;
    if (trap && trap.isConnected) {
      return trap;
    }
  } catch {
    // Ignore and fall back to document.body.
  }
  return document.body;
}

/**
 * Synchronous clipboard copy using a temporary <textarea> and
 * `document.execCommand('copy')`.
 *
 * This is the preferred path in modern Chrome for ads-web because:
 * - It only needs transient user activation (must stay synchronous within the
 *   originating click) and is not gated by the `clipboard-write` permissions
 *   policy, so it works in cross-origin iframes without `allow="clipboard-write"`.
 * - Calling `navigator.clipboard.writeText` when that policy blocks the feature
 *   logs an unsuppressable console "[Violation] Permissions policy violation"
 *   (see https://crbug.com/414348233) even if the rejection is caught. Preferring
 *   this path avoids that call entirely when it succeeds.
 *
 * `container` lets callers inside a focus-trapping modal/popover mount the
 * temporary textarea within the trap so focus is not stolen from it.
 */
function copyWithExecCommand(text: string, container?: HTMLElement | null): boolean {
  const previouslyFocused = document.activeElement as HTMLElement | null;
  const mountPoint = resolveMountPoint(container);

  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  // Keep the element in the viewport but visually imperceptible. A fully
  // off-screen element (e.g. left: -9999px) can prevent selection/copy in some
  // Chrome versions, so use a 1px, transparent, fixed-position box instead.
  textarea.style.position = 'fixed';
  textarea.style.top = '0';
  textarea.style.left = '0';
  textarea.style.width = '1px';
  textarea.style.height = '1px';
  textarea.style.padding = '0';
  textarea.style.border = 'none';
  textarea.style.outline = 'none';
  textarea.style.boxShadow = 'none';
  textarea.style.background = 'transparent';
  textarea.style.opacity = '0';

  mountPoint.appendChild(textarea);

  try {
    // Chrome copies the current selection, which requires the textarea to be
    // focused. `preventScroll` avoids a jarring scroll jump.
    textarea.focus({ preventScroll: true });
    textarea.select();
    textarea.setSelectionRange(0, text.length);
    return document.execCommand('copy');
  } catch {
    return false;
  } finally {
    if (textarea.parentNode) {
      textarea.parentNode.removeChild(textarea);
    }
    // Restore focus so we don't disrupt the user's context (e.g. an open
    // popover/dialog that owned focus before the copy).
    if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
      previouslyFocused.focus({ preventScroll: true });
    }
  }
}

/**
 * Reports whether the `clipboard-write` permissions-policy feature is enabled
 * for the current document.
 *
 * Used only before calling the async Clipboard API (as a last resort). When the
 * feature is disabled, Chrome blocks `navigator.clipboard.writeText` AND logs a
 * noisy "Permissions policy violation" that try/catch cannot suppress
 * (https://crbug.com/414348233). Detecting that up front skips the call.
 *
 * Detection strategy, in order:
 *   1. `document.permissionsPolicy` / `document.featurePolicy.allowsFeature`
 *      when exposed.
 *   2. Otherwise, if the document is inside an iframe (`window.self !==
 *      window.top`), assume NOT granted — modern Chrome often removes the
 *      Feature Policy API, and we cannot verify that the parent granted
 *      `allow="clipboard-write"`.
 *   3. At the top level with no Policy API, assume allowed.
 */
function isClipboardWriteAllowedByPolicy(): boolean {
  try {
    if (typeof document === 'undefined') {
      return true;
    }
    const doc = document as unknown as {
      featurePolicy?: { allowsFeature?: (feature: string) => boolean };
      permissionsPolicy?: { allowsFeature?: (feature: string) => boolean };
    };
    // Prefer the modern Permissions Policy API when both are present.
    const policy = doc.permissionsPolicy ?? doc.featurePolicy;
    if (policy && typeof policy.allowsFeature === 'function') {
      return policy.allowsFeature('clipboard-write');
    }

    if (typeof window !== 'undefined' && window.self !== window.top) {
      return false;
    }
  } catch {
    // Reading window.top can throw in a cross-origin iframe — which itself
    // means we are embedded, so treat clipboard-write as not granted.
    return false;
  }
  return true;
}

/**
 * Copies text to the system clipboard.
 *
 * Prefers the synchronous `execCommand('copy')` path first so that:
 *   1. The copy runs inside the originating user gesture (no prior `await`,
 *      which Chrome uses to revoke transient activation).
 *   2. We avoid calling `navigator.clipboard.writeText` when permissions
 *      policy would block it and log an unsuppressable console Violation.
 *
 * Falls back to the async Clipboard API only if `execCommand` fails and the
 * environment looks capable of using it.
 *
 * IMPORTANT: callers must invoke this directly from a user gesture handler
 * (e.g. `onClick`) without any preceding `await`.
 *
 * When the copy is triggered from inside a focus-trapping modal/popover (e.g.
 * MUI `Modal`/`Popover`), pass `options.container` (a ref to an element inside
 * the modal) so the temp textarea mounts inside the focus trap.
 */
export async function copyToClipboard(
  text: string,
  options?: { container?: HTMLElement | null }
): Promise<boolean> {
  if (!text) {
    return false;
  }

  const container = options?.container;

  // Prefer the sync path first — preserves user activation and avoids the
  // Permissions Policy violation that writeText logs when blocked.
  if (copyWithExecCommand(text, container)) {
    return true;
  }

  const canUseClipboardApi =
    typeof navigator !== 'undefined' &&
    !!navigator.clipboard?.writeText &&
    (typeof window === 'undefined' || window.isSecureContext !== false) &&
    isClipboardWriteAllowedByPolicy();

  if (canUseClipboardApi) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      return false;
    }
  }

  return false;
}
